/**
 * Pembacaan laporan DSP dan pemetaannya ke katalog lagu kita.
 *
 * Fungsi di sini murni: tidak menyentuh React, jaringan, maupun database.
 * Halaman Distribusi memanggilnya setelah berkas diunggah; tidak ada apa pun
 * yang disimpan — tahap ini hanya untuk memastikan pemetaannya benar.
 *
 * ── Yang diukur dari berkas contoh (Report Loka Publishing Mei 2026.xlsx) ──
 *
 * Bentuk nyatanya berbeda dari PRD di repo rujukan, dan yang dipakai di sini
 * adalah bentuk nyatanya:
 *
 *  - Ada EMPAT sheet, bukan satu: REKAP (ringkasan), lalu ADS REV, SUBS REV,
 *    dan ADJ SUBS REV sebagai data.
 *  - Judul kolom ada di baris KEDUA; baris pertama hanya berisi nama periode.
 *  - Kolomnya berbeda antar sheet: ADS memakai `Day` dan `Right Type`,
 *    sedangkan SUBS/ADJ memakai `Month` dan `Composition Right Type`.
 *  - Baris TERAKHIR tiap sheet adalah baris TOTAL: seluruh kolom kosong kecuali
 *    `Income Rev`. Kalau ikut terbaca, totalnya jadi dua kali lipat — inilah
 *    sebabnya penjumlahan mentah sheet ADS menghasilkan 1.713.476 padahal REKAP
 *    menyebut 856.738. Baris itu dibuang, dan nilainya justru dipakai sebagai
 *    pembanding rekonsiliasi.
 *  - `Income Rev` adalah dasar perhitungan, bukan `IDR Rev`. Dibuktikan dari
 *    REKAP: jumlah Income Rev per sheet sama persis dengan GRAND TOTAL di sana,
 *    sementara IDR Rev jauh lebih kecil (46.298 vs 856.738).
 *  - `Right Type` yang muncul hanya "Mechanical" dan "Reproduction".
 *  - `Writers` bisa berisi banyak nama sekaligus, dipisah koma (sekitar 20%
 *    baris, hingga 6 nama).
 */

/** Kode DSP. Laporan contoh seluruhnya YouTube. */
export const DSP_YOUTUBE = "YOUTUBE";

/** Nama DSP disamakan jadi kunci: "Tik Tok" / "TikTok" -> "tiktok". */
export const dspKey = (name) =>
  String(name || "").toLowerCase().replace(/\s+/g, "");

/**
 * DSP yang format laporannya sudah bisa dibaca.
 *
 * Baru YouTube. Tiap DSP menerbitkan susunan sheet dan nama kolom yang
 * berbeda, jadi pembacaannya tidak bisa diasumsikan sama — DSP lain tetap
 * ditawarkan di pemilih, tapi ditolak sebelum berkasnya dibaca daripada
 * menghasilkan angka yang salah diam-diam.
 */
export const SUPPORTED_DSP = ["youtube"];

export const isDspSupported = (name) => SUPPORTED_DSP.includes(dspKey(name));

/**
 * Tidak ada potongan rata untuk publisher.
 *
 * Sempat dipakai 0,85 mengikuti REKAP berkas contoh (ALFA PUSTAKA NADA 15% /
 * ELIRA PUBLISHING 85%). Itu keliru untuk data kita: porsi publisher SUDAH
 * menjadi salah satu baris komposisi hak tiap lagu (`creator_code '01'`, mis.
 * 30% pada Turah Wani), sehingga potongan rata di atasnya mengambil jatah
 * publisher dua kali — dan besarnya memang berbeda-beda per lagu, bukan satu
 * angka untuk semua.
 *
 * Pembagian 15/85 di REKAP itu lapisan lain: antar dua publisher penerima,
 * bukan antara publisher dan penciptanya. Kalau lapisan itu perlu dimodelkan,
 * tempatnya bukan di sini.
 */

/** Sheet data beserta jenis pendapatannya. REKAP bukan data baris. */
const DATA_SHEETS = [
  { match: /^ADS REV/i, type: "ads", label: "Ads Revenue" },
  { match: /^ADJ SUBS REV/i, type: "adj", label: "Adjustment Subscription" },
  { match: /^SUBS REV/i, type: "subs", label: "Subscription Revenue" },
];

/**
 * Jenis hak di laporan -> kolom persentase di `lisence_detail`.
 *
 * "Reproduction" dipetakan ke mekanikal: hak reproduksi memang hak mekanikal,
 * hanya penyebutannya yang berbeda di laporan YouTube.
 */
const RIGHT_TYPE_COLUMN = {
  mechanical: "mech",
  reproduction: "mech",
  performance: "perform",
  performing: "perform",
  synchronization: "sync",
  sync: "sync",
  demd: "demd",
};

/** Status pemetaan satu baris laporan. */
export const MATCH = {
  MATCHED: "matched",
  PARTIAL: "partial",
  CONFLICT: "conflict",
  UNMATCHED: "unmatched",
};

export const MATCH_LABEL = {
  [MATCH.MATCHED]: "Cocok",
  [MATCH.PARTIAL]: "Cocok Sebagian",
  [MATCH.CONFLICT]: "Konflik",
  [MATCH.UNMATCHED]: "Tidak Cocok",
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** Dibulatkan 2 desimal di setiap langkah agar penjumlahannya tetap konsisten. */
const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * Normalisasi nama untuk pencocokan: huruf kecil, tanpa gelar/IP base di depan,
 * spasi dirapatkan. Nama di `lisence_detail` kerap berawalan "I-0012345 - ".
 */
export const normalizeName = (value) =>
  String(value || "")
    .replace(/^[A-Z]?-?[0-9-]{6,}\s*-\s*/i, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** "A, B,C" -> ["A","B","C"]. Nama kosong dibuang. */
export const splitWriters = (value) =>
  String(value || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const sheetKind = (name) => DATA_SHEETS.find((s) => s.match.test(name)) || null;

/**
 * Baca satu workbook `xlsx` menjadi baris-baris yang seragam.
 *
 * @param {object} workbook hasil `XLSX.read`
 * @param {object} XLSX modul xlsx (disuntikkan agar modul ini tetap murni)
 * @returns {{periode: string, rows: Array, sheets: Array, warnings: Array}}
 */
export const parseDspReport = (workbook, XLSX) => {
  const warnings = [];
  const sheets = [];
  const rows = [];
  let periode = "";

  (workbook.SheetNames || []).forEach((name) => {
    const kind = sheetKind(name);
    if (!kind) return;

    const aoa = XLSX.utils.sheet_to_json(workbook.Sheets[name], {
      header: 1,
      blankrows: false,
    });

    // Baris 0 = nama periode, baris 1 = judul kolom, sisanya data.
    if (!periode && aoa[0] && aoa[0][0]) periode = String(aoa[0][0]);
    const header = aoa[1] || [];
    const body = aoa.slice(2);

    const idx = {};
    header.forEach((h, i) => {
      if (h) idx[String(h).trim()] = i;
    });

    const col = (row, ...names) => {
      for (const n of names) {
        if (idx[n] !== undefined) return row[idx[n]];
      }
      return undefined;
    };

    let sheetTotal = null;
    const parsed = [];

    body.forEach((raw, i) => {
      const assetId = col(raw, "Asset ID");
      const incomeRev = toNumber(col(raw, "Income Rev"));

      // Baris TOTAL: tanpa Asset ID, hanya Income Rev. Disimpan sebagai
      // pembanding, tidak ikut jadi data.
      if (!assetId) {
        if (incomeRev) sheetTotal = incomeRev;
        return;
      }

      parsed.push({
        id: `${kind.type}-${i}`,
        sheet: name,
        sheetType: kind.type,
        sheetLabel: kind.label,
        adjustmentType: col(raw, "Adjustment Type") || "None",
        periode: String(col(raw, "Day", "Month") ?? ""),
        country: col(raw, "Country") || "",
        assetId: String(assetId).trim(),
        assetTitle: col(raw, "Asset Title") || "",
        customId: String(col(raw, "Custom ID") || "").trim(),
        isrc: col(raw, "ISRC") || "",
        iswc: col(raw, "ISWC") || "",
        writersRaw: col(raw, "Writers") || "",
        writers: splitWriters(col(raw, "Writers")),
        rightType: String(
          col(raw, "Right Type", "Composition Right Type") || ""
        ).trim(),
        incomeRev,
        idrRev: toNumber(col(raw, "IDR Rev")),
      });
    });

    const computed = round2(parsed.reduce((s, r) => s + r.incomeRev, 0));
    const declared = sheetTotal === null ? null : round2(sheetTotal);

    // Rekonsiliasi tingkat sheet: jumlah baris yang kita baca harus sama dengan
    // angka TOTAL milik sheet itu sendiri. Kalau meleset, ada baris yang
    // terlewat atau terbaca dua kali — lebih baik ketahuan di sini.
    if (declared !== null && Math.abs(declared - computed) > 0.05) {
      warnings.push(
        `Sheet "${name}": total pada berkas ${declared.toLocaleString("id-ID")} ` +
          `tidak sama dengan jumlah barisnya ${computed.toLocaleString("id-ID")}.`
      );
    }

    sheets.push({
      name,
      type: kind.type,
      label: kind.label,
      rowCount: parsed.length,
      declaredTotal: declared,
      computedTotal: computed,
    });

    rows.push(...parsed);
  });

  if (rows.length === 0) {
    warnings.push(
      "Tidak ada sheet data yang dikenali. Berkas harus memuat sheet ADS REV, SUBS REV, atau ADJ SUBS REV."
    );
  }

  return { periode, rows, sheets, warnings };
};

/**
 * Susun data master jadi indeks siap-cari.
 *
 * @param {{songs: Array, assets: Array, rights: Array}} master
 */
export const buildMasterIndex = (master) => {
  const songs = master?.songs || [];
  const assets = master?.assets || [];
  const rights = master?.rights || [];

  const songById = new Map();
  const songByWorkId = new Map();
  songs.forEach((s) => {
    songById.set(s.song_id, s);
    if (s.work_id) songByWorkId.set(String(s.work_id).toLowerCase(), s);
  });

  // Kunci gabungan DSP + Asset ID: Asset ID yang sama di DSP berbeda adalah
  // data yang berbeda.
  const songByAsset = new Map();
  assets.forEach((a) => {
    const dsp = a.dsp_code || DSP_YOUTUBE;
    songByAsset.set(`${dsp}::${String(a.asset_id).trim()}`, a.song_id);
  });

  const rightsBySong = new Map();
  rights.forEach((r) => {
    if (!rightsBySong.has(r.song_id)) rightsBySong.set(r.song_id, []);
    rightsBySong.get(r.song_id).push(r);
  });

  // Nama pemegang hak -> daftar song_id, untuk pencocokan tahap 2.
  const songsByWriter = new Map();
  rights.forEach((r) => {
    const key = normalizeName(r.creator_name);
    if (!key) return;
    if (!songsByWriter.has(key)) songsByWriter.set(key, new Set());
    songsByWriter.get(key).add(r.song_id);
  });

  // Nama DSP -> kode internal kita. `song_asset.dsp_code` berisi kode dari
  // tabel `rt_dsp` ('06' untuk Youtube), sedangkan laporan hanya menyebut
  // namanya. Tanpa peta ini, kunci pencarian tahap 1 berada di ruang nama yang
  // berbeda dari kunci yang tersimpan, sehingga Asset ID yang benar pun tidak
  // pernah cocok.
  const dspCodeByName = new Map();
  (master?.dsp || []).forEach((d) => {
    const key = dspKey(d.dsp);
    if (key) dspCodeByName.set(key, String(d.dsp_code));
  });

  return {
    songById,
    songByWorkId,
    songByAsset,
    rightsBySong,
    songsByWriter,
    dspCodeByName,
  };
};

/** Kode internal untuk DSP sebuah laporan, mis. "YouTube" -> "06". */
export const resolveDspCode = (index, dspName = "Youtube") =>
  index.dspCodeByName.get(dspKey(dspName)) || null;

/**
 * Cocokkan baris laporan ke lagu kita lewat tiga tahap.
 *
 * Perbedaan yang disengaja dari PRD rujukan: PRD menetapkan baris hanya
 * "matched" bila ketiga tahap lolos. Pada data kita, tabel `song_asset` masih
 * kosong sehingga tahap 1 akan gagal untuk SEMUA baris, dan seluruh laporan
 * akan tampak tidak cocok — padahal lagunya sebenarnya dikenali lewat Custom ID.
 *
 * Karena itu ada status `partial`: ada tahap yang mengenali lagunya, tidak ada
 * yang bertentangan, tapi tidak semua tahap lolos. Baris begini tetap bisa
 * dihitung, dengan catatan tahap mana yang belum terpenuhi — sekaligus menjadi
 * daftar kerja data master apa yang perlu dilengkapi.
 */
export const matchRows = (rows, index, { dspName = "Youtube" } = {}) => {
  // Laporan hanya menyebut nama DSP-nya; kodenya diambil dari referensi kita.
  const dspCode = resolveDspCode(index, dspName);

  return rows.map((row) => {
    // Tahap 1 — DSP + Asset ID terdaftar pada sebuah lagu.
    // Kalau DSP laporan tidak ada di referensi, kuncinya mustahil cocok —
    // lebih baik null daripada diam-diam mencocokkan ke DSP yang salah.
    const assetSongId = dspCode
      ? index.songByAsset.get(`${dspCode}::${row.assetId}`) || null
      : null;

    // Tahap 3 — Custom ID laporan = Work ID (`song.id`) lagu.
    const workSongId =
      index.songByWorkId.get(row.customId.toLowerCase())?.song_id || null;

    // Lagu acuan: tahap 1 lebih dipercaya, lalu tahap 3.
    const songId = assetSongId || workSongId;

    // Tahap 2 — nama penulis di laporan ada di komposisi hak lagu tersebut.
    // Kolom Writers HANYA dipakai untuk pencocokan; pembagian uangnya nanti
    // tetap mengikuti komposisi di database kita.
    const writerNames = row.writers.map(normalizeName).filter(Boolean);
    const songWriters = songId
      ? new Set(
          (index.rightsBySong.get(songId) || [])
            .map((r) => normalizeName(r.creator_name))
            .filter(Boolean)
        )
      : new Set();

    const matchedWriters = [];
    const unknownWriters = [];
    row.writers.forEach((name, i) => {
      const key = writerNames[i];
      if (key && songWriters.has(key)) matchedWriters.push(name);
      else unknownWriters.push(name);
    });

    const stage1 = { ok: Boolean(assetSongId), songId: assetSongId };
    const stage2 = {
      ok: matchedWriters.length > 0,
      matched: matchedWriters,
      unknown: unknownWriters,
    };
    const stage3 = { ok: Boolean(workSongId), songId: workSongId };

    // Konflik: dua tahap sama-sama mengenali lagu, tapi lagunya berbeda.
    const isConflict =
      assetSongId && workSongId && assetSongId !== workSongId;

    // Status ditentukan HANYA oleh Work ID dan Asset ID.
    //
    // Nama penulis sengaja tidak ikut menentukan: ia tidak pernah menentukan
    // lagu mana yang dipakai (itu dari tahap 1/3), dan pembagian uangnya pun
    // mengikuti komposisi hak di database kita, bukan kolom Writers. Jadi
    // menjadikannya syarat hanya menurunkan status baris yang sebenarnya sudah
    // teridentifikasi pasti. Hasilnya tetap dilaporkan sebagai peringatan,
    // karena nama yang tidak dikenal adalah petunjuk Asset ID salah pasang.
    let status;
    let failedStage = null;
    if (isConflict) {
      status = MATCH.CONFLICT;
    } else if (!songId) {
      status = MATCH.UNMATCHED;
      failedStage = 1;
    } else if (stage1.ok && stage3.ok) {
      status = MATCH.MATCHED;
    } else {
      status = MATCH.PARTIAL;
      failedStage = !stage1.ok ? 1 : 3;
    }

    return {
      ...row,
      stage1,
      stage2,
      stage3,
      songId,
      song: songId ? index.songById.get(songId) || null : null,
      status,
      failedStage,
    };
  });
};

/**
 * Kunci penyesuaian porsi: per lagu + pemegang hak + jenis kolom.
 *
 * Kesepakatan pembagian berlaku per LAGU, bukan per baris laporan — satu lagu
 * muncul ratusan kali di laporan (per hari, per negara). Jadi penyesuaian
 * disimpan pada tingkat lagu dan otomatis berlaku untuk seluruh barisnya.
 */
export const overrideKey = (songId, creatorId, column) =>
  `${songId}::${creatorId}::${column}`;

/** Kolom persentase yang dipakai sebuah jenis hak, atau null bila tak dikenal. */
export const columnForRightType = (rightType) =>
  RIGHT_TYPE_COLUMN[String(rightType || "").toLowerCase()] || null;

/** Baris yang boleh ikut dihitung: lagunya dikenali dan tidak bertentangan. */
const isDistributable = (row) =>
  row.status === MATCH.MATCHED || row.status === MATCH.PARTIAL;

/**
 * Pecah tiap baris laporan menjadi satu baris per pemegang hak.
 *
 * Rumusnya `DIST_MR = Income Rev x persentase hak`, dengan kolom persentase
 * ditentukan jenis haknya.
 */
export const distributeRows = (
  matchedRows,
  index,
  { overrides = {} } = {}
) => {
  const distributions = [];
  const exceptions = [];

  matchedRows.forEach((row) => {
    if (!isDistributable(row)) return;

    const column = columnForRightType(row.rightType);
    if (!column) {
      exceptions.push({
        rowId: row.id,
        songId: row.songId,
        reason: `Jenis hak "${row.rightType || "(kosong)"}" belum dipetakan ke kolom pembagian.`,
        amount: row.incomeRev,
      });
      return;
    }

    const rights = index.rightsBySong.get(row.songId) || [];

    // Porsi yang berlaku: hasil penyesuaian bila ada, kalau tidak dari
    // komposisi di database. Diterapkan SEBELUM pembagian, supaya pemeriksaan
    // total 100% di bawah ikut menilai angka yang disesuaikan — bukan angka
    // asli yang sudah tidak dipakai.
    const pctOf = (r) => {
      const key = overrideKey(row.songId, r.creator_id, column);
      return key in overrides ? toNumber(overrides[key]) : toNumber(r[column]);
    };

    const totalPct = rights.reduce((s, r) => s + pctOf(r), 0);

    // BR-5: total persentase per jenis hak harus 100%. Kalau tidak, membagi
    // tetap akan membuat rekonsiliasi meleset — lebih baik ditahan dan
    // dilaporkan daripada diam-diam menghasilkan angka yang salah.
    if (Math.abs(totalPct - 100) > 0.01) {
      exceptions.push({
        rowId: row.id,
        songId: row.songId,
        songTitle: row.song?.song_title || row.assetTitle,
        reason:
          rights.length === 0
            ? "Lagu belum punya komposisi hak di katalog."
            : `Total ${column} lagu ini ${totalPct}%, bukan 100%.`,
        amount: row.incomeRev,
      });
      return;
    }

    // Seluruh pendapatan sumber dibagi menurut komposisi hak lagu itu; porsi
    // publisher termasuk di dalamnya.
    const base = row.incomeRev;
    rights.forEach((r) => {
      const pct = pctOf(r);
      if (pct <= 0) return;
      const original = toNumber(r[column]);
      distributions.push({
        rowId: row.id,
        sheetType: row.sheetType,
        periode: row.periode,
        country: row.country,
        songId: row.songId,
        workId: row.song?.work_id || row.customId,
        songTitle: row.song?.song_title || row.assetTitle,
        rightType: row.rightType,
        creatorId: r.creator_id,
        creatorName: r.creator_name,
        creatorCode: r.creator_code,
        ipBaseNumber: r.ip_base_number || "",
        percentage: pct,
        // Dipakai tabel untuk menandai baris yang porsinya sudah disesuaikan.
        originalPercentage: original,
        isAdjusted: pct !== original,
        rightColumn: column,
        sourceRev: row.incomeRev,
        // `amount` dibulatkan untuk ditampilkan dan dibayarkan; `amountRaw`
        // disimpan untuk rekonsiliasi. Tanpa pemisahan ini, pembulatan 12.000-an
        // baris menumpuk jadi selisih beberapa rupiah dan rekonsiliasi selalu
        // tampak meleset padahal pembagiannya benar.
        amount: round2(base * (pct / 100)),
        amountRaw: base * (pct / 100),
      });
    });
  });

  return { distributions, exceptions };
};

/** Jumlahkan hasil per pemegang hak, dikelompokkan seperti IPBASE NO di PRD. */
export const groupByCreator = (distributions) => {
  const map = new Map();
  distributions.forEach((d) => {
    const key = d.creatorId || d.ipBaseNumber || d.creatorName;
    if (!map.has(key)) {
      map.set(key, {
        key,
        creatorId: d.creatorId,
        creatorName: d.creatorName,
        ipBaseNumber: d.ipBaseNumber,
        creatorCode: d.creatorCode,
        songs: new Set(),
        bySong: new Map(),
        rows: 0,
        amount: 0,
      });
    }
    const agg = map.get(key);
    agg.songs.add(d.songId);
    agg.rows += 1;
    agg.amount += d.amount;

    // Rincian per lagu: dari mana saja total seorang pemegang hak berasal.
    if (!agg.bySong.has(d.songId)) {
      agg.bySong.set(d.songId, {
        songId: d.songId,
        workId: d.workId,
        songTitle: d.songTitle,
        percentage: d.percentage,
        isAdjusted: d.isAdjusted,
        rows: 0,
        amount: 0,
      });
    }
    const song = agg.bySong.get(d.songId);
    song.rows += 1;
    song.amount += d.amount;
    // Porsi terakhir yang terlihat; sama untuk seluruh baris lagu yang sama.
    song.percentage = d.percentage;
    song.isAdjusted = d.isAdjusted;
  });

  return Array.from(map.values())
    .map((x) => ({
      ...x,
      songCount: x.songs.size,
      amount: round2(x.amount),
      bySong: Array.from(x.bySong.values())
        .map((sg) => ({ ...sg, amount: round2(sg.amount) }))
        .sort((a, b) => b.amount - a.amount),
    }))
    .sort((a, b) => b.amount - a.amount);
};

/**
 * Rekonsiliasi: seluruh pendapatan sumber harus terjelaskan, baik sebagai
 * hasil distribusi, porsi publisher yang dipotong, maupun yang tertahan.
 *
 * Angka `selisih` TIDAK pernah dipaksa nol. Engine di repo rujukan menimpa
 * total sumber agar selisihnya nol begitu isunya "selesai"
 * (distributionEngine.ts baris 2377) — itu menyembunyikan ketidakcocokan yang
 * justru paling perlu dilihat.
 */
export const reconcile = (matchedRows, distributions, exceptions) => {
  const sum = (arr, pick) => round2(arr.reduce((s, x) => s + pick(x), 0));

  const totalSource = sum(matchedRows, (r) => r.incomeRev);
  const distributable = matchedRows.filter(isDistributable);
  const exceptionIds = new Set(exceptions.map((e) => e.rowId));

  const accounted = sum(
    distributable.filter((r) => !exceptionIds.has(r.id)),
    (r) => r.incomeRev
  );
  const totalDistributed = sum(distributions, (d) => d.amount);
  // Rekonsiliasi memakai nilai belum dibulatkan; selisih pembulatannya
  // dilaporkan tersendiri supaya terlihat, bukan tersembunyi dalam toleransi.
  const distributedRaw = distributions.reduce((s, d) => s + d.amountRaw, 0);
  const totalUnmatched = sum(
    matchedRows.filter((r) => !isDistributable(r)),
    (r) => r.incomeRev
  );
  const totalException = sum(exceptions, (e) => e.amount);

  const explainedRaw =
    distributedRaw +
    matchedRows.filter((r) => !isDistributable(r)).reduce((s, r) => s + r.incomeRev, 0) +
    exceptions.reduce((s, e) => s + e.amount, 0);

  const difference = round2(totalSource - explainedRaw);

  return {
    totalSource,
    accounted,
    totalDistributed,
    totalUnmatched,
    totalException,
    explained: round2(explainedRaw),
    difference,
    // Beda antara jumlah nilai yang dibulatkan dan yang belum — murni efek
    // pembulatan per baris, bukan pendapatan yang hilang.
    roundingDrift: round2(totalDistributed - distributedRaw),
    isBalanced: Math.abs(difference) <= 0.05,
  };
};

/** Ringkasan jumlah baris per status, untuk kartu statistik. */
export const summarizeMatch = (matchedRows) => {
  const byStatus = { matched: 0, partial: 0, conflict: 0, unmatched: 0 };
  const byFailedStage = { 1: 0, 3: 0 };
  const unknownAssets = new Set();
  const unknownWorkIds = new Set();
  // Peringatan, bukan kegagalan: baris yang lagunya sudah dikenali tapi nama
  // penulis di laporan tidak ada di komposisi kita.
  let writerMismatch = 0;

  matchedRows.forEach((r) => {
    byStatus[r.status] += 1;
    if (r.failedStage) byFailedStage[r.failedStage] += 1;
    if (!r.stage1.ok) unknownAssets.add(r.assetId);
    if (!r.stage3.ok && r.customId) unknownWorkIds.add(r.customId);
    if (r.songId && !r.stage2.ok) writerMismatch += 1;
  });

  return {
    total: matchedRows.length,
    byStatus,
    byFailedStage,
    unknownAssetCount: unknownAssets.size,
    unknownWorkIdCount: unknownWorkIds.size,
    writerMismatch,
  };
};

/**
 * Komposisi hak sebuah lagu untuk satu jenis kolom, dengan porsi yang berlaku.
 * Dipakai dialog penyesuaian agar seluruh pemegang hak bisa diatur sekaligus
 * dan totalnya tetap bisa dijaga 100%.
 */
export const rightsForAdjust = (master, songId, column, overrides = {}) => {
  const index = buildMasterIndex(master);
  return (index.rightsBySong.get(songId) || []).map((r) => {
    const key = overrideKey(songId, r.creator_id, column);
    return {
      creatorId: r.creator_id,
      creatorName: r.creator_name,
      creatorCode: r.creator_code,
      original: toNumber(r[column]),
      percentage: key in overrides ? toNumber(overrides[key]) : toNumber(r[column]),
    };
  });
};

/** Satu panggilan untuk seluruh alur: baca -> cocokkan -> bagi -> rekap. */
export const runDistribution = (rows, master, options = {}) => {
  const index = buildMasterIndex(master);
  const matchedRows = matchRows(rows, index, options);
  const { distributions, exceptions } = distributeRows(
    matchedRows,
    index,
    options
  );

  return {
    matchedRows,
    distributions,
    exceptions,
    perCreator: groupByCreator(distributions),
    reconciliation: reconcile(matchedRows, distributions, exceptions),
    summary: summarizeMatch(matchedRows),
  };
};
